import React from 'react'
import ReactDOM from 'react-dom/client'
import { Auth0Provider } from '@auth0/auth0-react'
import App from './App'
import './index.css'
import { getAuthMode } from './lib/env'
import { getAuth0Config, isAuth0Configured } from './lib/auth-config'

// Check if window.ENV is loaded
function isEnvLoaded(): boolean {
  return typeof (window as any).ENV !== 'undefined'
}

// Initialize the app with appropriate auth provider
function initializeApp() {
  const authMode = getAuthMode()
  const rootElement = document.getElementById('root')!

  console.log('[main.tsx] Initializing app with auth mode:', authMode)
  console.log('[main.tsx] window.ENV loaded:', isEnvLoaded())
  console.log('[main.tsx] window.ENV:', (window as any).ENV)

  if (authMode === 'AUTH0') {
    // Check if Auth0 is configured
    if (!isAuth0Configured()) {
      console.error(
        '[main.tsx] Auth0 configuration missing. Please set AUTH0_DOMAIN and AUTH0_CLIENT_ID environment variables.'
      )
      // Render error message with helpful instructions
      ReactDOM.createRoot(rootElement).render(
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Configuration Error</h1>
            <p className="text-gray-700 mb-4">
              Auth0 configuration is missing or incomplete.
            </p>
            <div className="text-left text-sm text-gray-600 mb-4 space-y-2">
              <p><strong>For local development:</strong></p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Check that <code className="bg-gray-100 px-1">public/env.js</code> exists</li>
                <li>Set valid <code className="bg-gray-100 px-1">AUTH0_CLIENT_ID</code> and <code className="bg-gray-100 px-1">AUTH0_DOMAIN</code></li>
                <li>Or switch to <code className="bg-gray-100 px-1">AUTH_MODE: 'API_KEY'</code> in env.js</li>
              </ol>
            </div>
            <p className="text-xs text-gray-500">
              Current: AUTH0_DOMAIN={isEnvLoaded() ? (window as any).ENV?.AUTH0_DOMAIN || 'not set' : 'ENV not loaded'},
              AUTH0_CLIENT_ID={isEnvLoaded() ? ((window as any).ENV?.AUTH0_CLIENT_ID ? 'set' : 'not set') : 'ENV not loaded'}
            </p>
          </div>
        </div>
      )
      return
    }

    // Get Auth0 configuration
    const auth0Config = getAuth0Config()

    // Render with Auth0Provider
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <Auth0Provider {...auth0Config}>
          <App />
        </Auth0Provider>
      </React.StrictMode>
    )
  } else {
    // API Key mode - no Auth0Provider needed
    console.log('[main.tsx] Starting in API_KEY mode')
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    )
  }
}

// Wait for env.js to load before initializing
// In dev mode, the module script might execute before env.js loads
let retryCount = 0
const MAX_RETRIES = 100 // 5 seconds max (50ms * 100)

function waitForEnvAndInitialize() {
  if (isEnvLoaded()) {
    console.log('[main.tsx] window.ENV is loaded, initializing app')
    initializeApp()
  } else {
    retryCount++
    console.log(`[main.tsx] window.ENV not loaded yet, waiting... (attempt ${retryCount}/${MAX_RETRIES})`)

    if (retryCount >= MAX_RETRIES) {
      console.error('[main.tsx] window.ENV failed to load after 5 seconds')
      // Show error to user
      const rootElement = document.getElementById('root')!
      ReactDOM.createRoot(rootElement).render(
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Configuration Error</h1>
            <p className="text-gray-700 mb-4">
              Failed to load runtime configuration (env.js).
            </p>
            <div className="text-left text-sm text-gray-600 mb-4 space-y-2">
              <p><strong>Possible issues:</strong></p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>File <code className="bg-gray-100 px-1">public/env.js</code> doesn't exist</li>
                <li>File has JavaScript syntax errors</li>
                <li>Browser console shows errors loading the file</li>
              </ol>
              <p className="mt-3"><strong>To fix:</strong></p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Check browser console for errors</li>
                <li>Verify <code className="bg-gray-100 px-1">public/env.js</code> exists</li>
                <li>Try running: <code className="bg-gray-100 px-1">./verify-local-setup.sh</code></li>
                <li>Restart dev server: <code className="bg-gray-100 px-1">npm run dev:web</code></li>
              </ol>
            </div>
          </div>
        </div>
      )
      return
    }

    // Retry after a short delay
    setTimeout(waitForEnvAndInitialize, 50)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', waitForEnvAndInitialize)
} else {
  waitForEnvAndInitialize()
}
